import { DropdownChangeParams } from 'primereact/dropdown'
import { useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

// local imports
import DashboardService from 'service/DashboardService'
import { IAllFirmList } from 'types/FirmType'
import {
  ChartItems,
  IOnCampusHealthServiceAllFirmsStatistics,
  IOnCampusHealthServiceFirmStatistics,
} from 'types/DashboardType'
import DateUtil from 'utils/DateUtil'
import { roundPercentages, uniqByProp } from 'utils/Miscellaneous'

interface IChartState {
  colors: string[]
  items: ChartItems | undefined
  labels: string[]
  footerTitle: string
  detailTitle: string
}

const chartColors = ['#465393', '#7986C6', '#CDD2EA']

/* Reducer */
const initialState: IChartState = {
  items: undefined,
  colors: chartColors,
  labels: [
    'dashboard.on-campus-tabs.personal',
    'dashboard.on-campus-tabs.group-lesson',
    'dashboard.on-campus-tabs.trainer',
  ],
  footerTitle: 'dashboard.on-campus-tabs.gym-all-footer-msg',
  detailTitle: 'dashboard.on-campus-tabs.gym-all-detail-msg',
}

function chartReducer(state: IChartState = initialState, action: { type: string; payload: { items: ChartItems } }) {
  const { type, payload } = action
  switch (type) {
    case 'all':
      return { ...initialState, ...payload }
    case 'personal':
      return {
        ...state,
        labels: ['dashboard.on-campus-tabs.active-res', 'dashboard.on-campus-tabs.canceled-res'],
        items: payload.items,
        colors: chartColors,
        detailTitle: 'dashboard.on-campus-tabs.gym-personal-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.gym-personal-footer-msg',
      }
    case 'group':
      return {
        ...state,
        labels: ['dashboard.on-campus-tabs.active-res', 'dashboard.on-campus-tabs.canceled-res'],
        items: payload.items,
        colors: chartColors,
        detailTitle: 'dashboard.on-campus-tabs.gym-group-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.gym-group-footer-msg',
      }
    case 'trainer':
      return {
        ...state,
        labels: ['dashboard.on-campus-tabs.active-res', 'dashboard.on-campus-tabs.canceled-res'],
        items: payload.items,
        colors: chartColors,
        detailTitle: 'dashboard.on-campus-tabs.gym-trainer-detail-msg',
        footerTitle: 'dashboard.on-campus-tabs.gym-trainer-footer-msg',
      }
    default:
      return initialState
  }
}

const fetchSumOfKey = (arr: any[], property: string): number => {
  return arr.map((item: any) => item[property]).reduce((prev: any, curr: any) => prev + curr, 0)
}

const percentage = (partialValue: number, total: number) => (100 * partialValue) / total

const useGetGymServiceChartData = (props: any) => {
  const { t } = useTranslation()

  const chartTypeOptions = [
    { key: 'all', value: t('dashboard.on-campus-tabs.all-type') },
    { key: 'personal', value: t('dashboard.on-campus-tabs.personal-type') },
    { key: 'group', value: t('dashboard.on-campus-tabs.group-type') },
    { key: 'trainer', value: t('dashboard.on-campus-tabs.trainer-type') },
  ]

  const [{ items, colors, labels, detailTitle, footerTitle }, dispatch] = useReducer(chartReducer, initialState)
  const [personalData, setPersonalData] = useState<IOnCampusHealthServiceAllFirmsStatistics | undefined>()
  const [groupData, setGroupData] = useState<IOnCampusHealthServiceAllFirmsStatistics | undefined>()
  const [trainerData, setTrainerData] = useState<IOnCampusHealthServiceAllFirmsStatistics | undefined>()
  const [chartType, setChartType] = useState('all')
  const [loading, setLoading] = useState(false)
  const [excelData, setExcelData] = useState<any>()

  const getAllChartsData = () => {
    if (!personalData || !groupData || !trainerData) return

    const personalCount = personalData?.totalCount || 0
    const groupCount = groupData?.totalCount || 0
    const trainerCount = trainerData?.totalCount || 0
    const totalGymCount = personalCount + groupCount + trainerCount

    const countPersonal =
      personalData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.totalCount) || []
    const countGroup = groupData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.totalCount) || []
    const countTrainer =
      trainerData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.totalCount) || []

    const percentPersonal = personalData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.totalCount, totalGymCount)
    })
    const percentGroup = groupData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.totalCount, totalGymCount)
    })
    const percentTrainer = trainerData?.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.totalCount, totalGymCount)
    })

    const totalLabels = [
      ...(trainerData?.firmStatistics || []),
      ...(personalData?.firmStatistics || []),
      ...(groupData?.firmStatistics || []),
    ]

    function mergeCountsAndLabels(
      countPersonal: number[] | [],
      countGroup: number[] | [],
      countTrainer: number[] | [],
      allFirmLabels: IOnCampusHealthServiceFirmStatistics[]
    ): any {
      return allFirmLabels?.map((firm, index) => ({
        firmName: firm.firmName,
        totalCount: totalGymCount,
        personal: countPersonal[index] || 0,
        group: countGroup[index] || 0,
        trainer: countTrainer[index] || 0,
      }))
    }

    const allFirmLabels = uniqByProp('firmName')(totalLabels || []) as IOnCampusHealthServiceFirmStatistics[]
    setExcelData(mergeCountsAndLabels(countPersonal, countGroup, countTrainer, allFirmLabels))

    const payload = {
      items: {
        doughnutItems: {
          datasets: [personalCount, groupCount, trainerCount],
          total: totalGymCount,
          totalPercentage: 0,
        },
        stackedItems: {
          labels: allFirmLabels?.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
          count: [countPersonal, countGroup, countTrainer],
          percentage: [
            roundPercentages(percentPersonal || [0]),
            roundPercentages(percentGroup || [0]),
            roundPercentages(percentTrainer || [0]),
          ],
        },
      },
    }
    dispatch({ type: 'all', payload })
  }

  const chooseChartDataWithType = (
    selectedType: string,
    data: IOnCampusHealthServiceAllFirmsStatistics | undefined
  ) => {
    if (!data) return

    const cameCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.cameCount)
    const notCameCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.notCameCount)
    // const cancelCount = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => i.cancelCount)
    const camePercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.cameCount, i.totalCount)
    })
    const notCamePercent = data.firmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
      return percentage(i.notCameCount, i.totalCount)
    })
    // const cancelPercent = data.FirmStatistics.map((i: IOnCampusHealthServiceFirmStatistics) => {
    //   return percentage(i.CancelCount, i.TotalCount)
    // })

    if (selectedType === 'personal') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.firmStatistics, 'cameCount'),
              fetchSumOfKey(data.firmStatistics, 'notCameCount'),
              // fetchSumOfKey(data.firmStatistics, 'cancelCount'),
            ],
            total: fetchSumOfKey(data.firmStatistics, 'totalCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cameCount, notCameCount], // cancelCount
            percentage: [
              roundPercentages(camePercent),
              roundPercentages(notCamePercent),
              // roundPercentages(cancelPercent),
            ],
          },
        },
      }
      dispatch({ type: 'personal', payload })
    } else if (selectedType === 'group') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.firmStatistics, 'cameCount'),
              fetchSumOfKey(data.firmStatistics, 'notCameCount'),
              // fetchSumOfKey(data.firmStatistics, 'cancelCount'),
            ],
            total: fetchSumOfKey(data.firmStatistics, 'totalCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cameCount, notCameCount], // cancelCount
            percentage: [
              roundPercentages(camePercent),
              roundPercentages(notCamePercent),
              // roundPercentages(cancelPercent),
            ],
          },
        },
      }
      dispatch({ type: 'group', payload })
    } else if (selectedType === 'trainer') {
      const payload = {
        items: {
          doughnutItems: {
            datasets: [
              fetchSumOfKey(data.firmStatistics, 'cameCount'),
              fetchSumOfKey(data.firmStatistics, 'notCameCount'),
              // fetchSumOfKey(data.firmStatistics, 'cancelCount'),
            ],
            total: fetchSumOfKey(data.firmStatistics, 'totalCount'),
            totalPercentage: 0,
          },
          stackedItems: {
            labels: data.firmStatistics.map((item: IOnCampusHealthServiceFirmStatistics) => item.firmName),
            count: [cameCount, notCameCount], // cancelCount
            percentage: [
              roundPercentages(camePercent),
              roundPercentages(notCamePercent),
              // roundPercentages(cancelPercent),
            ],
          },
        },
      }
      dispatch({ type: 'trainer', payload })
    }
  }

  const getChartData = () => {
    let idList = props.selectedFirms.map((item: IAllFirmList) => item.id)
    const request = {
      firmIds: idList,
      startDate: DateUtil.formatDateToString(props.startDate),
      endDate: DateUtil.formatDateToString(props.endDate),
    }
    const getPersonal = async () => {
      const resp = await DashboardService.getCampusGymPersonalServiceStatistics(request)
      const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
      setPersonalData({ ...resp.data, firmStatistics: array })
      if (chartType !== 'all') chooseChartDataWithType(chartType, { ...resp.data, firmStatistics: array })
    }
    const getGroup = async () => {
      const resp = await DashboardService.getCampusGymGroupServiceStatistics(request)
      const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
      setGroupData({ ...resp.data, firmStatistics: array })
      if (chartType !== 'all') chooseChartDataWithType(chartType, { ...resp.data, firmStatistics: array })
    }
    const getTrainer = async () => {
      const resp = await DashboardService.getCampusGymTrainerServiceStatistics(request)
      const array = resp.data.firmStatistics.filter((f) => idList.includes(f.firmId))
      setTrainerData({ ...resp.data, firmStatistics: array })
      if (chartType !== 'all') chooseChartDataWithType(chartType, { ...resp.data, firmStatistics: array })
    }

    setLoading(true)
    getPersonal()
      .then(() => getGroup().then(() => getTrainer()))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false))
  }

  /* Fetch Statistics */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => getChartData(), [props.selectedFirms, props.startDate, props.endDate])

  useEffect(() => {
    if (chartType === 'all') getAllChartsData()
    else if (chartType === 'personal') chooseChartDataWithType(chartType, personalData)
    else if (chartType === 'group') chooseChartDataWithType(chartType, groupData)
    else chooseChartDataWithType(chartType, trainerData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, groupData, personalData, trainerData])

  /* Chart Type select box handler */
  const handleChartTypeChange = (e: DropdownChangeParams) => {
    const selectedType = e.value
    setChartType(selectedType)
    if (selectedType === 'all') getAllChartsData()
    else {
      const selectedData =
        selectedType === 'personal' ? personalData : selectedType === 'group' ? groupData : trainerData
      chooseChartDataWithType(selectedType, selectedData)
    }
  }

  return {
    t,
    excelData,
    chartType,
    chartTypeOptions,
    handleChartTypeChange,
    loading,
    items,
    colors,
    labels,
    detailTitle,
    footerTitle,
  }
}

export default useGetGymServiceChartData
